/**
 * Markdown, as much of it as a page of prose needs.
 *
 * This exists so that the About panel can be written by whoever deploys the site, in a
 * file they drop next to `index.html` after the build. That makes the content arbitrary
 * text from outside the application, which decides almost everything about the shape of
 * this module.
 *
 * ## Why a parser of our own rather than a library
 *
 * Two reasons, and the second is the one that matters.
 *
 * The first is size. three.js is already most of the bundle and every byte of it is
 * needed before anything can be drawn; a markdown library would be paid for by every
 * visitor on every visit, to render one panel that most of them will dismiss.
 *
 * The second is that **this produces a tree, not a string of HTML**. A renderer that
 * hands back markup has to be trusted along with whatever it was given, and the receiving
 * end has to write it into the page with `innerHTML` — at which point a stray `<script>`
 * in the source file is a script that runs. What comes out of here is a list of nodes
 * describing what was written, and `ui/markdownView.ts` turns those into elements one
 * `createElement` at a time. There is no path from the text to executable markup, so the
 * question of whether the file can be trusted never has to be answered.
 *
 * URLs are the one exception to that, because an `href` is a place where text becomes
 * behaviour even when the element around it was built safely — see `safeUrl`.
 *
 * ## What is supported
 *
 * Headings, paragraphs, lists (nested, ordered or not), block quotes, fenced code,
 * horizontal rules, and inline emphasis, code spans, links, images and hard line breaks.
 * That is the set a page of prose with a picture in it needs.
 *
 * What is deliberately missing: tables, footnotes, HTML blocks, reference-style links,
 * setext headings, and indented code blocks. The last two are absent because they clash
 * with things that are present — `---` is a horizontal rule, and four spaces of indent
 * belongs to nested lists. This is not CommonMark and does not try to be; it is a subset
 * chosen so that text written by somebody who knows markdown does what they expect.
 */

/** A run of text, or something wrapped around one. */
export type Inline =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'code'; readonly text: string }
  | { readonly kind: 'emphasis'; readonly content: readonly Inline[] }
  | { readonly kind: 'strong'; readonly content: readonly Inline[] }
  | {
      readonly kind: 'link';
      readonly href: string;
      readonly title: string | undefined;
      readonly content: readonly Inline[];
    }
  | {
      readonly kind: 'image';
      readonly src: string;
      readonly alt: string;
      readonly title: string | undefined;
    }
  /** A line break the author asked for, with two trailing spaces or a backslash. */
  | { readonly kind: 'break' };

/** Something that occupies its own lines. */
export type Block =
  | {
      readonly kind: 'heading';
      readonly level: number;
      readonly content: readonly Inline[];
    }
  | { readonly kind: 'paragraph'; readonly content: readonly Inline[] }
  | {
      readonly kind: 'code';
      readonly language: string | undefined;
      readonly text: string;
    }
  | { readonly kind: 'quote'; readonly blocks: readonly Block[] }
  | {
      readonly kind: 'list';
      readonly ordered: boolean;
      /** What the first item is numbered, so `3.` starts at three. Always 1 unordered. */
      readonly start: number;
      /** Each item is a list of blocks, which is what lets lists nest. */
      readonly items: readonly (readonly Block[])[];
    }
  | { readonly kind: 'rule' };

/**
 * Schemes a link may use.
 *
 * An allow list rather than a block list. The attack is `javascript:`, but naming that
 * one and letting everything else through would leave `vbscript:`, `data:text/html` and
 * whatever the next browser invents; naming what is permitted instead means a scheme
 * nobody here has heard of is refused by default.
 */
const LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/**
 * Image data URLs that may be used inline, so a self-contained About page is possible.
 *
 * Raster formats only. SVG is absent on purpose: it is a document rather than a picture,
 * and although browsers do not run script in an SVG loaded through `<img>`, that is a
 * guarantee this module would rather not be resting on.
 */
const IMAGE_DATA = /^data:image\/(?:png|jpeg|gif|webp|avif);/i;

const SCHEME = /^([a-z][a-z0-9+.-]*):/i;

/**
 * Check a URL written in the source file, or refuse it.
 *
 * Anything relative passes: it resolves against the page, which is where the About file
 * came from, so a deployer can reference a picture sitting beside it. Anything with a
 * scheme has to be one of the few that only ever fetch or navigate.
 *
 * Returning `undefined` rather than a placeholder lets the caller degrade sensibly — a
 * refused link becomes its own text, a refused image becomes its alt text — instead of
 * putting a broken control on the page.
 */
export function safeUrl(raw: string, kind: 'link' | 'image'): string | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;

  const scheme = SCHEME.exec(trimmed);
  if (scheme === null) return trimmed;

  const name = `${(scheme[1] ?? '').toLowerCase()}:`;
  if (LINK_SCHEMES.has(name)) return trimmed;
  if (kind === 'image' && IMAGE_DATA.test(trimmed)) return trimmed;
  return undefined;
}

/** Read a markdown document. Never throws: anything unrecognised ends up as text. */
export function parseMarkdown(source: string): readonly Block[] {
  // Line endings first, so nothing below has to think about them, and tabs with them: an
  // indent is measured in spaces everywhere here, and a tab that stayed a tab would count
  // as one of them and drop a nested list back a level.
  const normalised = source.replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  return parseBlocks(normalised.split('\n'));
}

/** The text of a run of inline nodes, with the markup dropped. Used for image alts. */
export function plainText(content: readonly Inline[]): string {
  return content
    .map((node) => {
      switch (node.kind) {
        case 'text':
        case 'code':
          return node.text;
        case 'emphasis':
        case 'strong':
        case 'link':
          return plainText(node.content);
        case 'image':
          return node.alt;
        case 'break':
          return ' ';
      }
    })
    .join('');
}

const FENCE = /^ {0,3}(`{3,}|~{3,})[ \t]*(.*)$/;
const FENCE_END = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
const RULE = /^ {0,3}(?:(?:\*[ \t]*){3,}|(?:-[ \t]*){3,}|(?:_[ \t]*){3,})$/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?[ \t]*$/;
const QUOTE = /^ {0,3}>[ \t]?(.*)$/;
const BULLET = /^( *)([-*+])(?:[ \t]+(.*))?$/;
const ORDERED = /^( *)(\d{1,9})([.)])(?:[ \t]+(.*))?$/;

/** One list item's opening line, taken apart. */
interface Item {
  readonly indent: number;
  readonly ordered: boolean;
  readonly start: number;
  /** The marker itself — `-`, or `12.` — whose width sets where the content begins. */
  readonly marker: string;
  /** What followed the marker on the same line. */
  readonly rest: string;
}

function parseBlocks(lines: readonly string[]): readonly Block[] {
  const blocks: Block[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (line === undefined) break;

    if (isBlank(line)) {
      index += 1;
      continue;
    }

    const fence = FENCE.exec(line);
    if (fence !== null) {
      index = readCode(lines, index, fence[1] ?? '', blocks);
      continue;
    }

    // Before the list, because `- - -` and `***` are both rules and both would otherwise
    // read as a bullet with an odd label.
    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      index += 1;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading !== null) {
      const level = (heading[1] ?? '#').length;
      // A closing run of hashes is decoration, not content: `## Title ##`.
      const text = (heading[2] ?? '').replace(/[ \t]+#+$/, '');
      blocks.push({ kind: 'heading', level, content: parseInline(text) });
      index += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      index = readQuote(lines, index, blocks);
      continue;
    }

    if (matchItem(line) !== undefined) {
      index = readList(lines, index, blocks);
      continue;
    }

    index = readParagraph(lines, index, blocks);
  }

  return blocks;
}

function readCode(
  lines: readonly string[],
  at: number,
  marker: string,
  into: Block[],
): number {
  const opening = FENCE.exec(lines[at] ?? '');
  // Only the first word of the info string is used; the rest is for other tools.
  const language = (opening?.[2] ?? '').trim().split(/\s+/)[0];

  const body: string[] = [];
  let cursor = at + 1;

  while (cursor < lines.length) {
    const line = lines[cursor];
    if (line === undefined) break;
    if (closesFence(line, marker)) {
      cursor += 1;
      break;
    }
    body.push(line);
    cursor += 1;
  }

  // An unclosed fence runs to the end of the file rather than swallowing the document as
  // a paragraph. Whoever left the fence open still sees their code.
  into.push({
    kind: 'code',
    language: language === undefined || language === '' ? undefined : language,
    text: body.join('\n'),
  });
  return cursor;
}

function closesFence(line: string, marker: string): boolean {
  const end = FENCE_END.exec(line);
  if (end === null) return false;
  const run = end[1] ?? '';
  // The same character, and at least as long: a fence opened with backticks is not
  // closed by tildes, and a longer opening run needs a longer closing one.
  return run.startsWith(marker.slice(0, 1)) && run.length >= marker.length;
}

function readQuote(lines: readonly string[], at: number, into: Block[]): number {
  const body: string[] = [];
  let cursor = at;

  while (cursor < lines.length) {
    const line = lines[cursor];
    if (line === undefined) break;
    const quoted = QUOTE.exec(line);
    if (quoted === null) break;
    body.push(quoted[1] ?? '');
    cursor += 1;
  }

  // Recursion is what makes a quote able to hold paragraphs, lists, or another quote.
  into.push({ kind: 'quote', blocks: parseBlocks(body) });
  return cursor;
}

/**
 * Read a run of list items.
 *
 * The one subtlety is where an item ends. Everything indented as far as the item's own
 * content is part of it — which is what lets a nested list simply be one of the blocks
 * that item is made of, parsed by the same code with the indent taken off. A blank line
 * does not end the list on its own, because a list with a paragraph in one of its items
 * has blank lines inside it; it ends the list only when nothing that follows it is
 * another item.
 */
function readList(lines: readonly string[], at: number, into: Block[]): number {
  const opening = matchItem(lines[at] ?? '');
  const ordered = opening?.ordered ?? false;
  const start = opening?.start ?? 1;
  const items: (readonly Block[])[] = [];

  let index = at;
  let end = at;

  for (;;) {
    // Blanks are stepped over rather than consumed, so that a list which turns out to
    // have finished leaves them for whatever comes next.
    let scan = index;
    while (scan < lines.length && isBlank(lines[scan] ?? '')) scan += 1;

    const line = lines[scan];
    if (line === undefined) break;

    const item = matchItem(line);
    // Undefined lands here too: a line that is not an item ends the list, and so does an
    // item of the other kind.
    if (item?.ordered !== ordered) break;

    const contentIndent = item.indent + item.marker.length + 1;
    const body: string[] = [item.rest];
    let cursor = scan + 1;
    let blanks = 0;

    while (cursor < lines.length) {
      const next = lines[cursor];
      if (next === undefined) break;

      if (isBlank(next)) {
        blanks += 1;
        cursor += 1;
        continue;
      }

      if (indentOf(next) < contentIndent) break;

      for (let n = 0; n < blanks; n += 1) body.push('');
      blanks = 0;
      body.push(next.slice(contentIndent));
      cursor += 1;
    }

    items.push(parseBlocks(body));
    // Back over any blanks that turned out to be after the item rather than inside it.
    index = cursor - blanks;
    end = index;
  }

  into.push({ kind: 'list', ordered, start, items });
  return end;
}

function readParagraph(lines: readonly string[], at: number, into: Block[]): number {
  const body: string[] = [];
  let cursor = at;

  while (cursor < lines.length) {
    const line = lines[cursor];
    if (line === undefined) break;
    if (isBlank(line) || (cursor > at && startsBlock(line))) break;
    // Only the front is trimmed: two spaces at the end of a line are a hard break, and
    // trimming both ends here would quietly delete the one way to ask for one.
    body.push(line.trimStart());
    cursor += 1;
  }

  into.push({ kind: 'paragraph', content: parseInline(body.join('\n')) });
  return cursor;
}

/** Whether a line interrupts a paragraph already under way. */
function startsBlock(line: string): boolean {
  if (FENCE.test(line) || RULE.test(line) || HEADING.test(line) || QUOTE.test(line)) {
    return true;
  }

  const item = matchItem(line);
  if (item === undefined) return false;

  // A numbered item only interrupts if it starts at one, so a sentence wrapping onto a
  // line beginning "2024. It was a good year" stays part of its paragraph. An empty item
  // does not interrupt either: a line that is nothing but a dash is far more likely to be
  // punctuation than the start of a list.
  return item.rest !== '' && (!item.ordered || item.start === 1);
}

function matchItem(line: string): Item | undefined {
  const bullet = BULLET.exec(line);
  if (bullet !== null) {
    return {
      indent: (bullet[1] ?? '').length,
      ordered: false,
      start: 1,
      marker: bullet[2] ?? '-',
      rest: bullet[3] ?? '',
    };
  }

  const ordered = ORDERED.exec(line);
  if (ordered === null) return undefined;

  const digits = ordered[2] ?? '1';
  return {
    indent: (ordered[1] ?? '').length,
    ordered: true,
    start: Number.parseInt(digits, 10),
    marker: `${digits}${ordered[3] ?? '.'}`,
    rest: ordered[4] ?? '',
  };
}

function isBlank(line: string): boolean {
  return line.trim() === '';
}

function indentOf(line: string): number {
  return line.length - line.trimStart().length;
}

/* Inline parsing. */

/** What a match consumed, and what it produced. */
interface Match {
  readonly nodes: readonly Inline[];
  readonly next: number;
}

/**
 * The characters a backslash can hide.
 *
 * Restricted to punctuation markdown itself uses, so that a backslash in ordinary prose —
 * a Windows path, say — stays a backslash rather than vanishing and taking the next
 * letter's meaning with it.
 */
const ESCAPABLE = /[\\`*_{}[\]()#+\-.!>~|]/;

const TARGET =
  /^\((?:<([^<>]*)>|([^\s()]*))(?:[ \t\n]+(?:"([^"]*)"|'([^']*)'))?[ \t\n]*\)/;

const WORD = /[\p{L}\p{N}]/u;

function parseInline(text: string): readonly Inline[] {
  const nodes: Inline[] = [];
  let plain = '';
  let at = 0;

  const flush = (): void => {
    if (plain === '') return;
    nodes.push({ kind: 'text', text: plain });
    plain = '';
  };

  const take = (match: Match | undefined): boolean => {
    if (match === undefined) return false;
    flush();
    nodes.push(...match.nodes);
    at = match.next;
    return true;
  };

  while (at < text.length) {
    const char = text[at];
    if (char === undefined) break;

    if (char === '\\') {
      const next = text[at + 1];
      if (next !== undefined && ESCAPABLE.test(next)) {
        plain += next;
        at += 2;
        continue;
      }
      // A backslash at the end of a line is the other way of asking for a hard break.
      if (next === '\n') {
        flush();
        nodes.push({ kind: 'break' });
        at += 2;
        continue;
      }
    }

    if (char === '\n') {
      // Two spaces before the newline mean the break was asked for. Anything else is a
      // line ending that only exists because the source file was wrapped, and HTML will
      // collapse it into the space it should have been.
      if (/ {2,}$/.test(plain)) {
        plain = plain.replace(/[ \t]+$/, '');
        flush();
        nodes.push({ kind: 'break' });
      } else {
        plain += '\n';
      }
      at += 1;
      continue;
    }

    // Code first, so that a backtick span containing asterisks is code rather than
    // emphasis wearing backticks.
    if (char === '`' && take(matchCode(text, at))) continue;
    if (char === '!' && text[at + 1] === '[' && take(matchImage(text, at))) continue;
    if (char === '[' && take(matchLink(text, at))) continue;
    if ((char === '*' || char === '_') && take(matchEmphasis(text, at))) continue;

    plain += char;
    at += 1;
  }

  flush();
  return nodes;
}

function matchCode(text: string, at: number): Match | undefined {
  const run = runLength(text, at, '`');
  const from = at + run;
  let scan = from;

  // The closing run has to be exactly as long as the opening one, which is what lets a
  // span written with two backticks hold a single one.
  while (scan < text.length) {
    if (text[scan] !== '`') {
      scan += 1;
      continue;
    }

    const here = runLength(text, scan, '`');
    if (here !== run) {
      scan += here;
      continue;
    }

    let content = text.slice(from, scan);
    // One space either side is padding rather than content — how ``` ` ``` is written.
    if (content.length > 2 && content.startsWith(' ') && content.endsWith(' ')) {
      content = content.slice(1, -1);
    }

    return {
      nodes: [{ kind: 'code', text: content.replace(/\n/g, ' ') }],
      next: scan + here,
    };
  }

  return undefined;
}

function matchLink(text: string, at: number): Match | undefined {
  const label = matchLabel(text, at);
  if (label === undefined) return undefined;

  const target = TARGET.exec(text.slice(label.next));
  if (target === null) return undefined;

  const next = label.next + target[0].length;
  const content = parseInline(label.text);
  const href = safeUrl(target[1] ?? target[2] ?? '', 'link');

  // A link whose destination was refused becomes its own words. Silently dropping the
  // text as well would lose the sentence around it; leaving a dead control on the page
  // would be worse than either.
  if (href === undefined) return { nodes: content, next };

  return {
    nodes: [{ kind: 'link', href, title: target[3] ?? target[4], content }],
    next,
  };
}

function matchImage(text: string, at: number): Match | undefined {
  const label = matchLabel(text, at + 1);
  if (label === undefined) return undefined;

  const target = TARGET.exec(text.slice(label.next));
  if (target === null) return undefined;

  const next = label.next + target[0].length;
  const alt = plainText(parseInline(label.text));
  const src = safeUrl(target[1] ?? target[2] ?? '', 'image');

  // The alt text is what an image is worth when it cannot be shown, which is exactly the
  // situation a refused source leaves us in.
  if (src === undefined) {
    return { nodes: alt === '' ? [] : [{ kind: 'text', text: alt }], next };
  }

  return { nodes: [{ kind: 'image', src, alt, title: target[3] ?? target[4] }], next };
}

/** The bracketed part of a link or image, from its `[` to the `]` that closes it. */
function matchLabel(
  text: string,
  at: number,
): { readonly text: string; readonly next: number } | undefined {
  let depth = 0;
  let scan = at;

  while (scan < text.length) {
    const char = text[scan];
    if (char === undefined) break;

    if (char === '\\') {
      scan += 2;
      continue;
    }

    if (char === '[') depth += 1;
    if (char === ']') {
      depth -= 1;
      // Counted rather than matched on the first `]`, so a link whose text contains an
      // image — which is how a picture is made clickable — closes in the right place.
      if (depth === 0) return { text: text.slice(at + 1, scan), next: scan + 1 };
    }

    scan += 1;
  }

  return undefined;
}

/**
 * Emphasis, and the rules that keep it from firing on things that are not emphasis.
 *
 * Three of them, and each one exists because of a real sentence it would otherwise
 * mangle: a delimiter with a space just inside it opens nothing, so `2 * 3 * 4` is
 * arithmetic; an underscore touching a word neither opens nor closes, so `file_name_here`
 * survives; and a closing run longer than the opening one is consumed from its end, which
 * is what turns `***both***` into emphasis nested inside strong rather than a stray
 * asterisk on the outside.
 */
function matchEmphasis(text: string, at: number): Match | undefined {
  const char = text[at];
  if (char === undefined) return undefined;

  const width = Math.min(runLength(text, at, char), 2);
  const from = at + width;

  const opens = text[from];
  if (opens === undefined || /\s/.test(opens)) return undefined;
  if (char === '_' && isWord(text[at - 1])) return undefined;

  let scan = from;
  while (scan < text.length) {
    const here = text[scan];
    if (here === undefined) break;

    if (here === '\\') {
      scan += 2;
      continue;
    }

    if (here !== char) {
      scan += 1;
      continue;
    }

    const run = runLength(text, scan, char);
    if (run < width) {
      scan += run;
      continue;
    }

    const closeAt = scan + run - width;
    if (closeAt === from || /\s/.test(text[closeAt - 1] ?? ' ')) {
      scan += run;
      continue;
    }
    if (char === '_' && isWord(text[closeAt + width])) {
      scan += run;
      continue;
    }

    const content = parseInline(text.slice(from, closeAt));
    return {
      nodes: [{ kind: width === 2 ? 'strong' : 'emphasis', content }],
      next: closeAt + width,
    };
  }

  return undefined;
}

function runLength(text: string, at: number, char: string): number {
  let length = 0;
  while (text[at + length] === char) length += 1;
  return length;
}

function isWord(char: string | undefined): boolean {
  return char !== undefined && WORD.test(char);
}
